/* ADD FILE */
/* UNTAPPED - FABRICS PRICE PER METRE */
import { onAddElement, observeSettledContent } from './stripes-utils.js';
const { FABRIC_UNIT_FACTOR, LENGTH_PRICE_LABEL } = await import(window.STRIPES_CONSTANTS_URL);

/**
 * Find price fields inside .is-sold-by-length
 * 
 * Whenever the value is updated, convert it to metres and redisplay, reusing the same currency format, 
 * and adding a /m suffix.
 * 
 * Save the new converted value, so new changed values can be checked against it to see if this is a 
 * real background change.
 * 
 * We stop the underlying Kalles price from flashing before we update it by initially hiding it, then
 * setting the class 'price--ready' after we have updated.
 * 
 * However, there is still an issue with trade price the sequence is:
 * - Kalles sets the initial value
 * - We update it to the Stripes value
 * - WPD updates it to the trade value
 * - We update it to the Stripes trade value
 * 
 * This means it flashes the initial Stripes value and we have no way of knowing whether 
 * it will subsequently be updated or not.  We therefore use a timer to delay revealing it and 
 * hope tha the trade price update has completed in that time.
 * 
 * Note: the compare-at price (.hdt-compare-at-price) is only visible in the non-trade case.
 * When trade (WPD) is active it hides that element and renders its own compare-at as an <s>
 * inside hdt-price, which is handled by enforceConvertedTradePrice instead.
 * We therefore skip .hdt-money elements inside hdt-compare-at-price in the onAddElement
 * callback and handle them as a side-effect of their sibling hdt-price's .hdt-money call.
 */


onAddElement({
  root: document.body,
  selector: '.is-sold-by-length .hdt-money',
  onNode(moneyEl) {
    // Compare-at is handled as a side-effect of its sibling hdt-price's .hdt-money call
    if (moneyEl.closest('hdt-compare-at-price')) return;
    enforceConvertedMoney(moneyEl);
  }
});

function enforceConvertedMoney(moneyEl) {
    console.log("MONEY ELEMENT", moneyEl);
  const card = moneyEl.closest('hdt-card-product');
  if (card){
    const titleLink = card.querySelector('a.hdt-card-product__title');
    const productName = titleLink?.textContent?.trim() ?? null;
    // console.log("CONVERTING NON-TRADE: ", productName);
  }

  const priceEl = moneyEl.closest('.hdt-price, hdt-qty-price');
  if (!priceEl) return;

  if (!priceEl.closest('.is-sold-by-length')) return;

  // --- Convert the main price ---
  convertMoneyEl(moneyEl, priceEl);

  // --- Convert the compare-at price if present ---
  // hdt-compare-at-price is a sibling of hdt-price inside .hdt-price__sale / .hdt-price__list
  const container = priceEl.closest('.hdt-price__container, .hdt-price__sale, .hdt-price__list');
  if (container) {
    const compareAtMoneyEl = container.querySelector('hdt-compare-at-price .hdt-money');
    if (compareAtMoneyEl) {
      convertMoneyEl(compareAtMoneyEl, priceEl);
    }
  }
}

/**
 * Converts a single .hdt-money element to per-metre and marks the price block ready.
 * Idempotent: skips work if the value is already correct.
 * Only writes stripesPricePerMetreCents for the sale price, not the compare-at.
 *
 * @param {Element} moneyEl  - the .hdt-money span to convert
 * @param {Element} priceEl  - the hdt-price / hdt-qty-price ancestor
 */
function convertMoneyEl(moneyEl, priceEl) {
  const originalText = moneyEl.textContent;

  const baseValue = parseDisplayedMoney(originalText);
  if (baseValue == null) return;

  const converted = baseValue * FABRIC_UNIT_FACTOR;
  const formatted = formatLike(originalText, converted, LENGTH_PRICE_LABEL);

  const last = moneyEl.dataset.convertedValue;
  if (last === formatted) {
    // Invariant already holds → ensure ready
    priceEl.classList.add('price--ready');
    return;
  }

  // Apply conversion
  moneyEl.textContent = formatted;
  moneyEl.dataset.convertedValue = formatted;

  // Save the per-metre price to add to the cart (sale price only, not compare-at)
  if (!moneyEl.closest('hdt-compare-at-price')) {
    priceEl.dataset.stripesPricePerMetreCents = String(Math.round(converted * 100));
  }

  // Mark ready only once invariant is enforced
  setTimeout(()=>{
    console.log("TIMEOUT UP - SET READY", moneyEl);
    priceEl.classList.add('price--ready');
  }, 900);
}

function parseDisplayedMoney(text) {
  const match = text.match(/[-\d.,]+/);
  if (!match) return null;
  const token = match[0];

  const lastComma = token.lastIndexOf(',');
  const lastDot = token.lastIndexOf('.');

  let normalized;
  if (lastComma > lastDot) {
    normalized = token.replace(/\./g, '').replace(',', '.');
  } else {
    normalized = token.replace(/,/g, '');
  }

  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
}

/**
 * Format a number in the same way (like) the 1st parameter, eg
 * formatLike("£35.90", 71.80) → "£71.80"
 * formatLike("abc€35,90def", 71.80) → "abc€71,80def"
 * 
 * A units string can also be passed in which is placed immediately after the number
 */
function formatLike(originalText, newValue, label = '') {
  const match = originalText.match(/[-\d.,]+/);
  if (!match) return originalText;

  const usesCommaDecimal = match[0].lastIndexOf(',') > match[0].lastIndexOf('.');
  let number = newValue.toFixed(2);
  if (usesCommaDecimal) number = number.replace('.', ',');

  // Insert the label immediately after the number, not at the end of the
  // whole string — needed now that WPD's VAT breakdown puts trailing text
  // (" inc. VAT", " exc. VAT)") after the amount.
  return originalText.replace(/[-\d.,]+/, number + label);
}


/**
 * TRADE
 * 
 * The WSH Wholesale Pricing injects a different html structure with no .htd-money element.  It also ends up being 
 * much more dynamic with multiple async updates to htd-price, thus we use the observeSettledContent to wait for 
 * the content to settle down.
 */

onAddElement({
  root: document.body,
  selector: '.is-sold-by-length hdt-price',
  onNode(priceEl) {
    // Attach noisy-content observer per price
    observeSettledContent({
      root: priceEl,
      debounce: 50,
      onSettle: enforceConvertedTradePrice
    });
  }
});

function enforceConvertedTradePrice(priceEl) {
  console.log("TRADE PRICE ELEMENT", priceEl);
  if (!(priceEl.classList.contains('wpd-processed') || priceEl.hasAttribute('data-wpd-cart-item'))) return;
  console.log(" - it's a wpd node");

  const compareEl = priceEl.querySelector('s.wpd-ext-compare-at-price');
  if (compareEl) convertTradePriceElement(compareEl);

  const inclEl = priceEl.querySelector('.wpd-tax-incl-price');
  if (inclEl) {
    const converted = convertTradePriceElement(inclEl);
    if (converted != null) {
      priceEl.classList.add('price--ready');
      priceEl.dataset.stripesPricePerMetreCents = String(Math.round(converted * 100));
    }
  } else {
    // Legacy fallback: main price as a bare text node with no VAT breakdown.
    const textNodes = [...priceEl.childNodes].filter(
      n => n.nodeType === Node.TEXT_NODE && n.textContent.trim()
    );
    if (textNodes.length) {
      const priceNode = textNodes[textNodes.length - 1];
      const priceText = priceNode.textContent;
      if (!priceText.includes(LENGTH_PRICE_LABEL)) {
        const baseValue = parseDisplayedMoney(priceText);
        if (baseValue != null) {
          const converted = baseValue * FABRIC_UNIT_FACTOR;
          priceNode.textContent = formatLike(priceText, converted, LENGTH_PRICE_LABEL);
          priceEl.classList.add('price--ready');
          priceEl.dataset.stripesPricePerMetreCents = String(Math.round(converted * 100));
        }
      }
    }
  }

  const exclEl = priceEl.querySelector('.wpd-tax-excl-price');
  if (exclEl) convertTradePriceElement(exclEl);
}

/**
 * Converts one WPD price element's text in place, unless it's already been
 * converted. Idempotency is checked against the rendered text itself (not
 * a dataset flag) because WPD periodically re-injects this whole subtree —
 * a dataset flag would be wiped by that, but the already-converted text
 * survives for us to detect.
 *
 * @returns {number|null} the converted value, or null if nothing changed
 */
function convertTradePriceElement(el) {
  const text = el.textContent;
  if (text.includes(LENGTH_PRICE_LABEL)) return null;

  const baseValue = parseDisplayedMoney(text);
  if (baseValue == null) return null;

  const converted = baseValue * FABRIC_UNIT_FACTOR;
  el.textContent = formatLike(text, converted, LENGTH_PRICE_LABEL);
  return converted;
}